import React, { useState, useEffect, useCallback } from 'react'
import { useForm, Controller } from 'react-hook-form'
import toast from 'react-hot-toast'
import { useDropzone } from 'react-dropzone'
import PhoneInput from 'react-phone-input-2'

import Drawer from '@mui/material/Drawer'
import Box from '@mui/material/Box'
import Grid from '@mui/material/Grid'
import TextField from '@mui/material/TextField'
import FormControl from '@mui/material/FormControl'
import FormHelperText from '@mui/material/FormHelperText'
import List from '@mui/material/List'
import ListItem from '@mui/material/ListItem'
import { styled } from '@mui/material/styles'
import IconButton from '@mui/material/IconButton'
import Typography from '@mui/material/Typography'

import Close from 'mdi-material-ui/Close'
import FileDocumentOutline from 'mdi-material-ui/FileDocumentOutline'

import DefaultPalette from '@core/theme/palettes'
import FormButtons from 'views/components/formbuttons'
import FormHeader from 'views/components/formheader'
import APICall from 'network/APICall'
import EndPoints from 'network/Endpoints'

import '../../../assets/css/phoneInput.css'
import { isSpaceValidateTextField } from '@core/utils/Const'
import Loader from '@core/helper/Loader'

const customTheme = DefaultPalette()

const Img = styled('img')(({ theme }) => ({
  [theme.breakpoints.up('md')]: {
    marginRight: theme.spacing(5),
    marginLeft: theme.spacing(2)
  },
  [theme.breakpoints.down('md')]: {
    marginBottom: theme.spacing(2)
  },
  [theme.breakpoints.down('sm')]: {
    width: 200
  }
}))

const HeadingTypography = styled(Typography)(({ theme }) => ({
  marginBottom: theme.spacing(1),
  marginTop: theme.spacing(1),
  [theme.breakpoints.down('sm')]: {
    marginBottom: theme.spacing(2)
  }
}))

const AddTeam = props => {
  const { open, toggle, type, heading, onSubmitTeamMember, updateData } = props
  const [files, setFiles] = useState([])
  const [filename, setFilename] = useState('')
  const [countryCode, setCountryCode] = useState('')
  const [phoneError, setPhoneError] = useState(false)
  const [removeImage, setRemoveImage] = useState(false)

  const { getRootProps, getInputProps } = useDropzone({
    maxFiles: 1,
    maxSize: 2000000,
    accept: {
      'image/*': ['.png', '.jpg', '.jpeg']
    },
    onDrop: acceptedFiles => {
      setFiles(acceptedFiles.map(file => Object.assign(file)))
      setValue('image', acceptedFiles)
      setRemoveImage(false)
    },
    onDropRejected: () => {
      setRemoveImage(true)
      toast.error('You can only upload 1 file & maximum size of 2 MB.', {
        duration: 2000
      })
    }
  })

  const renderFilePreview = file => {
    if (file.type.startsWith('image')) {
      return (
        <img
          width={38}
          height={38}
          alt={file.name}
          src={URL.createObjectURL(file)}
        />
      )
    } else {
      return <FileDocumentOutline />
    }
  }

  const handleRemoveFile = () => {
    setFiles([])
    setValue('image', '')
    setFilename('')
    setRemoveImage(true)
  }

  const defaultValues = {
    name: '',
    image: '',
    country_code: '',
    phone: '',
    designation: '',
    priority: 1
  }

  const {
    setValue,
    setError,
    clearErrors,
    reset,
    control,
    handleSubmit,
    formState: { errors }
  } = useForm({ defaultValues })

  const resetToEmptyValues = useCallback(() => {
    setValue('name', '')
    setValue('image', '')
    setValue('designation', '')
    setValue('phone', '91')
    setValue('country_code', '')
    setValue('priority', 1)
    setFiles([])
  }, [setValue])

  const resetToStoredValues = useCallback(() => {
    if (type === 'edit') {
      const event = {
        name: updateData?.name,
        image: updateData?.image,
        designation: updateData?.designation,
        phone: `${updateData?.country_code}${updateData?.phone_no}`,
        priority: updateData?.priority
      }
      setCountryCode(updateData?.country_code || '')
      setFilename(event.image || '')
      setValue('name', event.name || '')
      setValue('image', event.image || '')
      setValue('designation', event.designation || '')
      setValue('phone', event.phone || '91')
      setValue('priority', event.priority || 1)
    }
  }, [setValue, type, updateData])

  useEffect(() => {
    if (type === 'edit') {
      resetToStoredValues()
    } else {
      resetToEmptyValues()
    }
  }, [open, resetToStoredValues, resetToEmptyValues, type])

  const handleClose = () => {
    clearErrors()
    if (type === 'add') {
      reset(defaultValues)
      setFiles([])
      setValue('phone', '')
    }
    setRemoveImage(false)
    toggle()
  }

  const submitTeam = (data, selectImage) => {
    const phoneNumber = countryCode ? data.phone.slice(countryCode.length) : ''
    Loader.isLoading(true);
    if (type === 'add') {
      const payload = {
        name: data.name,
        designation: data.designation,
        phoneNo: phoneNumber,
        countryCode: countryCode,
        image: files.length > 0 ? selectImage : '',
        priority: data.priority
      }

      APICall('post', payload, EndPoints.addTeamMember)
        .then(response => {
          const { isError, message } = response.data
          if (!isError) {
            toast.success(message)
            onSubmitTeamMember()
            handleClose()
          } else {
            toast.error(message)
          }
          Loader.isLoading(false)
        })
        .catch(error => {
          Loader.isLoading(false)
          return error
        })
    } else {
      const payload = {
        id: updateData.id,
        name: data.name,
        designation: data.designation,
        phoneNo: phoneNumber,
        countryCode: !countryCode ? updateData.country_code : countryCode,
        image: !selectImage ? '' : selectImage,
        isRemoveImage: removeImage,
        priority: data.priority
      }
      APICall('post', payload, EndPoints.updateTeamMember)
        .then(response => {
          const { isError, message } = response.data
          if (!isError) {
            toast.success(message)
            onSubmitTeamMember()
            handleClose()
          } else {
            toast.error(message)
          }
          Loader.isLoading(false)
        })
        .catch(error => {
          Loader.isLoading(false)
          return error
        })
    }
  }

  const onSubmit = values => {
    if (!values.image || files.length === 0) {
      submitTeam(values, '')
      return
    }
    Loader.isLoading(true)
    const formData = new FormData()
    formData.append('image', values.image[0])
    formData.append('type', 'committee')

    APICall('post', formData, EndPoints.uploadImage)
      .then(response => {
        const { isError, data, message } = response.data
        if (!isError) {
          submitTeam(values, data.fileName)
        } else {
          toast.error(message)
        }
      })
      .catch(error => {
        return error
      })
      .finally(() => {
        Loader.isLoading(false)
      })
  }

  const fileList = files.map(file => (
    <ListItem
      key={file.name}
      sx={{
        justifyContent: 'space-between',
        display: 'flex'
      }}
    >
      <div
        className='file-details'
        style={{ display: 'flex', alignItems: 'center', width: '90%' }}
      >
        <div className='file-preview'>{renderFilePreview(file)}</div>
        <div style={{ marginLeft: 2, flexDirection: 'column', width: '85%' }}>
          <Typography
            className='file-name'
            variant='body2'
            sx={{ wordWrap: 'break-word' }}
          >
            {file.name}
          </Typography>
          <Typography className='file-size' variant='body2'>
            {Math.round(file.size / 100) / 10 > 1000
              ? `${(Math.round(file.size / 100) / 10000).toFixed(1)} mb`
              : `${(Math.round(file.size / 100) / 10).toFixed(1)} kb`}
          </Typography>
        </div>
      </div>
      <IconButton onClick={() => handleRemoveFile()}>
        <Close fontSize='small' />
      </IconButton>
    </ListItem>
  ))

  return (
    <Drawer
      open={open}
      anchor='right'
      variant='temporary'
      onClose={handleClose}
      ModalProps={{ keepMounted: true }}
      sx={{ '& .MuiDrawer-paper': { width: { xs: 300, sm: 400 } } }}
    >
      <FormHeader heading={heading} onClick={handleClose} />
      <Box sx={{ p: 5 }}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Grid item container spacing={4}>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <Controller
                  name='name'
                  control={control}
                  rules={{
                    required: true,
                    validate: isSpaceValidateTextField,
                  }}
                  render={({ field: { value, onChange } }) => (
                    <TextField
                      value={value}
                      onChange={onChange}
                      fullWidth
                      type='text'
                      label='Name'
                      placeholder='Enter name'
                      error={Boolean(errors.name)}
                      aria-describedby='validation-basic-first-name'
                    />
                  )}
                />
                {errors.name && (
                  <FormHelperText
                    sx={{ color: 'error.main' }}
                    id='validation-basic-first-name'
                  >
                    This field is required
                  </FormHelperText>
                )}
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <FormControl fullWidth>
                <Controller
                  name='phone'
                  control={control}
                  rules={{ required: false }}
                  render={({ field: { value } }) => (
                    <PhoneInput
                      country={'in'}
                      value={value}
                      autoFormat
                      masks={{ au: '... ... ... .' }}
                      countryCodeEditable={false}
                      onChange={(value, data, event) => {
                        setCountryCode(data.dialCode)
                        setValue('phone', value)
                        setPhoneError(false)
                        clearErrors('phone')
                      }}
                      error={Boolean(errors.phone)}
                      onBlur={(e, country) => {
                        if (value && value !== countryCode && countryCode) {
                          let _length = country.format.split('.').length - 1
                          if (value.length !== _length) {
                            let _error = { ...errors }
                            _error.isMobileEmpty = true
                            setPhoneError(true)
                            setError('phone', '')
                          }
                        }
                      }}
                      inputClass={errors.phone && 'form-control-error'}
                      buttonStyle={{
                        borderTopLeftRadius: '8px',
                        borderBottomLeftRadius: '8px',
                        margin: errors.phone ? 1 : 0
                      }}
                      containerStyle={{
                        height: '56px'
                      }}
                      inputStyle={{
                        width: '100%',
                        borderRadius: '8px',
                        color: 'rgba(76, 78, 100, 0.87)',
                        fontSize: '1rem',
                        fontWeight: 400,
                        lineHeight: '1.4375em',
                        alignItems: 'center',
                        height: '56px'
                      }}
                    />
                  )}
                />
                {errors.phone && (
                  <FormHelperText
                    sx={{ color: 'error.main' }}
                    id='validation-basic-first-name'
                  >
                    {phoneError && 'Please enter valid phone number'}
                  </FormHelperText>
                )}
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <FormControl fullWidth>
                <Controller
                  name='designation'
                  control={control}
                  rules={{
                    required: true,
                    validate: isSpaceValidateTextField,
                  }}
                  render={({ field: { value, onChange } }) => (
                    <TextField
                      fullWidth
                      label='Designation'
                      placeholder='Enter designation'
                      value={value}
                      onChange={onChange}
                      type='text'
                      error={Boolean(errors.designation)}
                      aria-describedby='validation-basic-first-name'
                    />
                  )}
                />
                {errors.designation && (
                  <FormHelperText
                    sx={{ color: 'error.main' }}
                    id='validation-basic-first-name'
                  >
                    This field is required
                  </FormHelperText>
                )}
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              {files.length === 0 && type === 'edit' && filename ? (
                <Grid item xs={12}>
                  <Box
                    sx={{
                      borderWidth: 1,
                      borderStyle: 'dashed',
                      borderColor: customTheme.grey.A200,
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      padding: 2,
                      display: 'flex',
                      cursor: 'pointer'
                    }}
                  >
                    <Img width={100} alt='Upload img' src={updateData.image} />
                    <IconButton onClick={() => handleRemoveFile()}>
                      <Close fontSize='small' />
                    </IconButton>
                  </Box>
                </Grid>
              ) : files.length > 0 ? (
                <div>
                  <Box
                    sx={{
                      borderWidth: 1,
                      borderRadius: 1,
                      borderStyle: 'dashed',
                      borderColor: customTheme.grey.A200
                    }}
                  >
                    <List>{fileList}</List>
                  </Box>
                </div>
              ) : (
                <FormControl fullWidth>
                  <Controller
                    name='image'
                    control={control}
                    rules={{ required: false }}
                    render={() => (
                      <div {...getRootProps({ className: 'dropzone' })}>
                        <input {...getInputProps()} />
                        <Box
                          sx={{
                            display: 'flex',
                            flexDirection: ['column', 'column', 'row'],
                            alignItems: 'center',
                            borderWidth: 1,
                            borderRadius: 1,
                            borderStyle: 'dashed',
                            borderColor: customTheme.grey.A200
                          }}
                        >
                          <Img
                            width={100}
                            alt='Upload img'
                            src='/images/misc/upload.png'
                          />
                          <Box
                            sx={{
                              display: 'flex',
                              flexDirection: 'column',
                              textAlign: ['center', 'center', 'inherit']
                            }}
                          >
                            <HeadingTypography>
                              Drop file here or click to upload.
                            </HeadingTypography>
                            <Typography color='textSecondary' variant='body2'>
                              Allowed *.jpeg, *.jpg, *.png
                            </Typography>
                            <Typography color='textSecondary' variant='body2'>
                              Max 1 file and max size of 2 MB
                            </Typography>
                          </Box>
                        </Box>
                      </div>
                    )}
                  />
                </FormControl>
              )}
            </Grid>

            <Grid item xs={12}>
              <FormControl fullWidth>
                <Controller
                  name='priority'
                  control={control}
                  rules={{ required: false }}
                  render={({ field: { value, onChange } }) => (
                    <TextField
                      fullWidth
                      label='Priority'
                      placeholder='Enter priority'
                      value={value}
                      onChange={onChange}
                      type='number'
                      inputProps={{
                        min: '0'
                      }}
                      error={Boolean(errors.priority)}
                      aria-describedby='validation-basic-first-name'
                    />
                  )}
                />
                {errors.priority && (
                  <FormHelperText
                    sx={{ color: 'error.main' }}
                    id='validation-basic-first-name'
                  >
                    This field is required
                  </FormHelperText>
                )}
              </FormControl>
            </Grid>

            <FormButtons type={type} resetFunction={resetToEmptyValues} />
          </Grid>
        </form>
      </Box>
    </Drawer>
  )
}

export default AddTeam
